import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fundReceipts, type FundReceiptListItem } from '@/api/fund_receipts';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const now = new Date();
const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

export default function FundReceiptsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState('');
  // Фільтр «Період» — дефолт поточний (стандарт ЗП-сторінок).
  const [fpy, setFpy] = useState<number | ''>(now.getFullYear());
  const [fpm, setFpm] = useState<number | ''>(now.getMonth() + 1);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const list = useQuery({
    queryKey: ['fund-receipts', orgId],
    queryFn: () => fundReceipts.list({ limit: 200, organization_id: orgId || undefined }),
  });
  const rows = useMemo(() => (list.data?.items ?? []).filter((r) =>
    (fpy === '' || r.period_year === fpy) && (fpm === '' || r.period_month === fpm)
  ), [list.data, fpy, fpm]);

  const cols = useMemo<ColumnDef<FundReceiptListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 140, sortValue: (r) => r.document_number, cell: (r) => r.document_number },
    { key: 'organization', header: 'Організація', width: 180, sortValue: (r) => orgName.get(r.organization_id) ?? '', cell: (r) => orgName.get(r.organization_id) ?? '—' },
    { key: 'document_date', header: 'Дата', width: 150, sortValue: (r) => r.document_date, cell: (r) => new Date(r.document_date).toLocaleDateString('uk-UA') },
    { key: 'period', header: 'Період', width: 140, sortValue: (r) => r.period_year * 100 + r.period_month, cell: (r) => `${MONTHS[r.period_month]} ${r.period_year}` },
    { key: 'fund_marker', header: 'Фонд', width: 90, cell: (r) => r.fund_marker === 'WORK_ACCIDENT_FUND' ? 'Фонд НВ' : 'ПФУ' },
    { key: 'total_amount', header: 'Сума', width: 130, align: 'right', sortValue: (r) => Number(r.total_amount), cell: (r) => fmt2(r.total_amount) },
    { key: 'posted', header: 'Проведено', width: 110, align: 'center', sortValue: (r) => r.posted ? 1 : 0, cell: (r) => (r.posted ? '✓ так' : 'ні') },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Надходження від Фонду</h2>
        <button className="primary" onClick={() => navigate('/fund-receipts/new')}>
          + Нове надходження
        </button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ minWidth: 200, margin: 0 }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— усі —</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 200 }}>
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={fpm} onChange={(e) => setFpm(e.target.value ? Number(e.target.value) : '')}
              style={{ flex: 2 }}>
              <option value="">— усі —</option>
              {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={fpy} onChange={(e) => setFpy(e.target.value ? Number(e.target.value) : '')}
              style={{ flex: 1 }}>
              <option value="">— усі —</option>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <DataTable<FundReceiptListItem>
        rows={rows}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/fund-receipts/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </div>
  );
}
